import { CreditCard, Wallet, Calendar } from 'lucide-react';
import { EmptyState } from '@/components/ui/empty-state';
import { useTranslations } from '@/i18n/use-locale';
import { useLocalizedName } from '@/i18n/localized-name';
import { useFormatSar } from '@/lib/format';
import { QueryGate } from '@/components/ui/query-gate';
import { usePaymentsQuery, usePlansQuery } from '@/lib/queries';
import { StatusBadge } from '@/components/ui/status-badge';
import { paymentTone } from '@/lib/status';
import { useEnumLabel } from '@/lib/labels';
import { Card } from '@/components/ui/card';
import { PageHeader } from '@/components/admin/page-header';
import { DataTable, type DataTableColumn } from '@/components/admin/data-table';
import type { AdminPayment } from '@/contracts';

export function SubscriptionsRoute() {
  const t = useTranslations('admin.subscriptions');
  const sar = useFormatSar();
  const localizedName = useLocalizedName();
  // Each falls back to the value as sent, for a rail or a status the console has no words for.
  const methodLabel = useEnumLabel('admin.subscriptions.method');
  const paymentStatusLabel = useEnumLabel('admin.subscriptions.paymentStatus');
  // The three paid tiers by wire id: only used until the plans load, or for a plan the
  // API no longer lists; the API's own name wins otherwise.
  const tierLabel = useEnumLabel('admin.subscriptions.planName');
  // The same query Settings edits, so a renamed plan reads the same on both screens.
  const plansQuery = usePlansQuery();
  const paymentsQuery = usePaymentsQuery();

  return (
    <QueryGate query={paymentsQuery}>
      {(payData) => {
        const planName = (planId: string): string => {
          const plan = plansQuery.data?.find((row) => row.id === planId);
          if (plan) return localizedName(plan);
          const tier = tierLabel(planId);
          return tier !== planId ? tier : t('planFallback', { id: planId });
        };
        const columns: ReadonlyArray<DataTableColumn<AdminPayment>> = [
          {
            key: 'reference',
            header: t('table.reference'),
            cellClassName: 'num font-bold text-navy',
            cell: (payment) => payment.reference,
          },
          {
            key: 'subscriber',
            header: t('table.subscriber'),
            cellClassName: 'font-semibold text-navy',
            cell: (payment) => payment.payerName,
          },
          {
            key: 'plan',
            header: t('table.planAmount'),
            cell: (payment) => (
              <>
                <p className="font-semibold text-navy">{planName(payment.planId)}</p>
                <p className="num text-[11.5px] text-conc-500">
                  {sar(Math.round(payment.amountMinor / 100))}
                </p>
              </>
            ),
          },
          {
            key: 'method',
            header: t('table.method'),
            cellClassName: 'text-conc-600',
            cell: (payment) => (payment.method ? methodLabel(payment.method) : '—'),
          },
          {
            key: 'status',
            header: t('columns.status'),
            cell: (payment) => (
              <StatusBadge tone={paymentTone(payment.status)}>
                {paymentStatusLabel(payment.status)}
              </StatusBadge>
            ),
          },
        ];
        const netRevenue = Math.round(payData.summary.netRevenueMinor / 100);
        const pendingCount = payData.summary.pendingCount;

        return (
          <div className="space-y-6">
            <PageHeader title={t('pageTitle')} sub={t('pageHint')} />

            {/* Summary Cards */}
            <div className="grid gap-4 sm:grid-cols-3">
              <Card className="p-5">
                <div className="flex items-center justify-between">
                  <span className="text-[12.5px] font-semibold text-conc-500">
                    {t('summary.net')}
                  </span>
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-pitch-50 text-pitch">
                    <Wallet className="h-5 w-5" />
                  </span>
                </div>
                <p className="num mt-2 text-2xl font-bold text-navy">{sar(netRevenue)}</p>
              </Card>

              <Card className="p-5">
                <div className="flex items-center justify-between">
                  <span className="text-[12.5px] font-semibold text-conc-500">
                    {t('summary.paid')}
                  </span>
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-navy-50 text-navy">
                    <CreditCard className="h-5 w-5" />
                  </span>
                </div>
                <p className="num mt-2 text-2xl font-bold text-navy">{payData.summary.paidCount}</p>
              </Card>

              <Card className="p-5">
                <div className="flex items-center justify-between">
                  <span className="text-[12.5px] font-semibold text-conc-500">
                    {t('summary.unmatched')}
                  </span>
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-gold-50 text-gold-700">
                    <Calendar className="h-5 w-5" />
                  </span>
                </div>
                <p className="num mt-2 text-2xl font-bold text-navy">{pendingCount}</p>
              </Card>
            </div>

            {/* Transactions Table */}
            <Card className="overflow-hidden shadow-sm">
              <DataTable
                caption={t('pageTitle')}
                rows={payData.items}
                columns={columns}
                rowKey={(payment) => payment.id}
                empty={
                  <EmptyState
                    icon={CreditCard}
                    title={t('ledgerEmpty.title')}
                    hint={t('ledgerEmpty.hint')}
                  />
                }
              />
            </Card>
          </div>
        );
      }}
    </QueryGate>
  );
}
